import { useId, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { SCHEDULING_CATEGORY_NAME_MAX } from '@mg/shared';
import { Button } from '../../components/ui/Button';
import { Input, Label } from '../../components/ui/Field';
import { actionErrorText } from '../../lib/api';
import { cn } from '../../lib/cn';
import { availableColors } from '../categories/categoryPalette';
import { SwatchPicker } from './SwatchPicker';
import { categoryNamed, isReservedName, RESERVED_NAME_ERROR } from './scheduleDraft';

/**
 * A category's name and colour, inline: the "Add category" form in the sidebar, and the same form for
 * renaming or recolouring from the selected category's heading.
 *
 * - The name is trimmed, required, and at most 30 characters (the input stops there). It must be new in
 *   the workspace ignoring case, and it cannot be "General" in any case (the built-in schedule's name):
 *   the form says so before asking the server, and shows the server's own refusal (a name taken
 *   elsewhere meanwhile) in the same place, as an alert.
 * - The colours on offer are the palette minus those the OTHER categories use (all ten once the others
 *   use every one); a new category starts on the first one, a renamed one on its own, which is always
 *   on offer, so a rename never changes the colour by itself.
 * - Escape cancels, wherever the focus is in the form. The caller decides where the focus goes next,
 *   because this form is gone by then.
 */
export function CategoryForm({
  label,
  categories,
  editing,
  submitLabel,
  onSubmit,
  onCancel,
  className,
}: {
  /** Names the form for assistive tech: "Add category", "Edit category". */
  label: string;
  /** Every category in the editor: the names a new name must not repeat, and the colours in use. */
  categories: ReadonlyArray<{ id: string; name: string; color: string }>;
  /** The category being renamed or recoloured; absent when adding one. */
  editing?: { id: string; name: string; color: string };
  submitLabel: string;
  /** Saves; rejects with the reason to show (the server's 409, say). The caller closes the form. */
  onSubmit: (name: string, color: string) => Promise<void>;
  onCancel: () => void;
  className?: string;
}) {
  const nameId = useId();
  const colorLabelId = useId();
  const errorId = useId();
  const nameRef = useRef<HTMLInputElement>(null);

  const colors = useMemo(() => availableColors(categories, editing?.id), [categories, editing?.id]);
  const [name, setName] = useState(editing?.name ?? '');
  const [color, setColor] = useState(
    () => colors.find((c) => c.toUpperCase() === editing?.color.toUpperCase()) ?? colors[0],
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const trimmed = name.trim();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!trimmed || busy) return;
    // The built-in schedule's name: the server refuses it with these words, for a new category and a
    // rename alike.
    if (isReservedName(trimmed)) {
      setError(RESERVED_NAME_ERROR);
      nameRef.current?.focus();
      return;
    }
    const taken = categoryNamed(categories, trimmed, editing?.id);
    if (taken) {
      setError(`A category named "${taken.name}" already exists.`);
      nameRef.current?.focus();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSubmit(trimmed, color);
    } catch (err) {
      setError(actionErrorText(err, 'Could not save the category.'));
      setBusy(false);
      nameRef.current?.focus();
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    e.stopPropagation();
    onCancel();
  };

  return (
    <form
      aria-label={label}
      onSubmit={(e) => void submit(e)}
      onKeyDown={onKeyDown}
      className={cn('space-y-3', className)}
    >
      <div>
        <Label htmlFor={nameId}>
          Category name
          <span aria-hidden className="ml-0.5 text-red-500 dark:text-error-dark">
            *
          </span>
        </Label>
        <Input
          ref={nameRef}
          id={nameId}
          // Opening the form is asking to type: the name box takes the focus.
          autoFocus
          required
          autoComplete="off"
          maxLength={SCHEDULING_CATEGORY_NAME_MAX}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setError(null);
          }}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
        />
        {error && (
          <p id={errorId} role="alert" className="mt-1 text-xs text-red-600 dark:text-error-dark">
            {error}
          </p>
        )}
      </div>
      <div>
        <p id={colorLabelId} className="mb-1 block text-sm font-medium text-ink-strong">
          Color
        </p>
        <SwatchPicker colors={colors} value={color} onChange={setColor} labelledBy={colorLabelId} />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" size="sm" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={!trimmed} loading={busy}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
